import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Fade, Tooltip } from '@mui/material'
import _ from 'lodash'
import { GanttCellRendererProps } from './index'
import GanttDate from './ganttDate'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import objects from '../../../../../../../../../utils/objects'
import { useGanttIndex } from './useGanttIndex'
import { IRowNode } from 'ag-grid-community'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { ProjectPlanCumulation } from '../../../../../../../../../lib/functions/projectPlan'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { Progress } from '../../../../../../../../pages/ProjectPlanNew/gridOptions/cumulation'
import {
  SliderWrapper,
  ProgressGauge,
  InsideLabel,
  RightLabel,
  LeftLabel,
  BarHoverArea,
  StyledSlider,
  StyledSliderThumb,
} from './ganttSliderStyles'
import { determineLabelMode, WbsItemForDisplay } from './useLabelMode'
import { useScrollTracking } from './useScrollTracking'
import { isGanttSummaryType } from '../ganttUtil'
import {
  GanttLabelContent,
  hasAnyContent,
  buildHoverText,
} from './GanttLabelContent'

const startEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.add('sevend-ag-cell-gantt-chart-editing')
}
const finishEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.remove('sevend-ag-cell-gantt-chart-editing')
}
export interface GanttSliderProps extends GanttCellRendererProps {
  displayOptions?: GanttDisplayOptions
  aggregateField?: AggregateField
  aggregateTarget?: AggregateTarget
  cumulation?: ProjectPlanCumulation
  overrunLeft?: number
  overrunWidth?: number
  wbsItemForDisplay?: WbsItemForDisplay
}

const calcProgressPositionPercent = (
  scheduledStartDate: Date,
  scheduledEndDate: Date,
  progressRate: number,
  timeScale: CalendarDateVO[]
): number => {
  if (timeScale.length === 0) return 0
  const startMs = scheduledStartDate.getTime()
  const DAY_MS = 86_400_000
  const endMs = scheduledEndDate.getTime() + DAY_MS
  if (endMs <= startMs) return 0

  const progressMs = startMs + (endMs - startMs) * progressRate
  const firstMs = timeScale[0].date.getTime()

  if (progressMs < firstMs) return 0

  const lastMs = timeScale[timeScale.length - 1].date.getTime()
  if (progressMs > lastMs) return 100

  const unitWidth = 100 / timeScale.length
  for (let i = 0; i < timeScale.length; i++) {
    const dateMs = timeScale[i].date.getTime()
    if (progressMs <= dateMs) {
      if (i === 0) return 0
      const prevMs = timeScale[i - 1].date.getTime()
      const fraction =
        dateMs === prevMs ? 0 : (progressMs - prevMs) / (dateMs - prevMs)
      return (i - 1 + fraction) * unitWidth
    }
  }
  return (timeScale.length - 1) * unitWidth
}

export const GanttSlider = (props: GanttSliderProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerLabelRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [cellWidth, setCellWidth] = useState(0)
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    setCellWidth(el.offsetWidth)
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCellWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const dataPath = props.dataPath ? props.dataPath + '.' : ''
  const getWbsItem = (data?: Record<string, unknown>) => {
    return objects.getValue(data ?? props.data, `${dataPath}wbsItem`)
  }

  const getTargetNodes = useCallback(
    () =>
      props.customEditTargetNodeSelection?.getTargetNodes(props) ||
      props.api?.getSelectedNodes(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- propsオブジェクト全体を指定しつつ個別プロパティも明示。AG Grid ICellRendererParamsの参照は安定
    [props, props.api, props.customEditTargetNodeSelection?.getTargetNodes]
  )

  const startEditingTargetRows = useCallback(() => {
    const selectedRows = getTargetNodes()
    if (selectedRows && selectedRows.length > 0) {
      selectedRows.forEach(row => startEditing(row))
    } else {
      startEditing(props.node)
    }
    return selectedRows
  }, [getTargetNodes, props.node])

  const ganttDate = useMemo(
    () =>
      new GanttDate({
        scale: props.timeScale,
        dateTerm: getWbsItem().scheduledDate,
      }),
    [props.value, props.parameter]
  )

  const aggregateField =
    props.aggregateField ?? AggregateField.WBS_ITEM_WORKLOAD
  const aggregateTarget = props.aggregateTarget ?? WbsItemType.TASK
  const { progressRate, hasChildrenToAggregate } = useMemo(() => {
    if (!props.isV2 || !props.cumulation) {
      return { progressRate: 0, hasChildrenToAggregate: false }
    }
    const progress = new Progress(
      props.cumulation,
      aggregateTarget,
      aggregateField
    )
    return {
      progressRate: progress.progressRate ?? 0,
      hasChildrenToAggregate: progress.total > 0,
    }
  }, [props.isV2, props.cumulation, aggregateField, aggregateTarget])

  const progressEndPercent = useMemo(() => {
    if (progressRate <= 0) return 0
    return calcProgressPositionPercent(
      ganttDate.startDate.date,
      ganttDate.endDate.date,
      progressRate,
      props.timeScale
    )
  }, [ganttDate.startDate, ganttDate.endDate, progressRate, props.timeScale])

  const ganttIndex = useGanttIndex(ganttDate)
  const sliderValue = useRef<number[]>([])
  sliderValue.current = ganttIndex.value

  useEffect(() => {
    if (getWbsItem().scheduledDate) {
      ganttIndex.initialize(ganttDate.startIndex, ganttDate.endIndex)
    }
  }, [props.value, props.parameter])

  const onEdit = useCallback(() => {
    if (!props.editable) return
    const movedRows = ganttIndex.getMovingRows()
    try {
      if (movedRows.length > 0) {
        movedRows.forEach(row => finishEditing(row))
      } else {
        finishEditing(props.node)
      }

      const updated = ganttIndex.edit(_.cloneDeep(props.data), dataPath)
      updated && props.updateRows(updated)
    } finally {
      props.customEditTargetNodeSelection?.rememberTargetNodes?.(movedRows)
    }
  }, [ganttIndex.value, props.customEditTargetNodeSelection])

  const onBulrSlider = useCallback(
    () => props.customEditTargetNodeSelection?.clearTargetNodes?.(),
    [props.customEditTargetNodeSelection]
  )

  const trackLeftPercent = (ganttIndex.value[0] / props.timeScale.length) * 100
  const trackRightPercent = (ganttIndex.value[1] / props.timeScale.length) * 100
  const barWidthPercent =
    Math.min(100, trackRightPercent) - Math.max(0, trackLeftPercent)
  // オーバーラン（遅延線）がある場合、right ラベルはオーバーランの右側に配置
  const barEndPercent =
    props.overrunLeft != null && props.overrunWidth != null
      ? props.overrunLeft + props.overrunWidth
      : trackRightPercent
  const isBarVisible =
    ganttIndex.value[0] < props.timeScale.length && ganttIndex.value[1] > 0

  const rightSpacePercent = 100 - barEndPercent
  const leftSpacePercent = Math.max(0, trackLeftPercent)

  const wbsItem = getWbsItem()
  const displayOptions = props.displayOptions
  const wbsItemForDisplay = props.wbsItemForDisplay
  const isParentType = isGanttSummaryType(wbsItem.wbsItemType)

  const labelMode = determineLabelMode({
    isV2: !!props.isV2,
    isBarVisible,
    isParentType,
    isDragging,
    cellWidth,
    barWidthPercent,
    rightSpacePercent,
    leftSpacePercent,
    displayOptions,
    wbsItemForDisplay,
    progressRate,
    hasCumulation: hasChildrenToAggregate && !wbsItem.wbsItemType?.isTask(),
  })

  useScrollTracking(wrapperRef, innerLabelRef, {
    enabled: labelMode === 'inside' && !!props.isV2,
    trackLeftPercent,
    trackRightPercent,
    displayOptions,
  })

  if (
    (props.timeScale.length < ganttDate.startIndex &&
      props.timeScale.length < ganttDate.endIndex) ||
    (ganttDate.startIndex < 0 && ganttDate.endIndex < 0)
  ) {
    return <></>
  }

  const slider = (
    <StyledSlider
      key={`gantt-slider-${props.node?.rowIndex}`}
      value={ganttIndex.value}
      size="small"
      components={{ Thumb: StyledSliderThumb }}
      valueLabelDisplay="auto"
      valueLabelFormat={(v, index) => {
        return index === 0
          ? ganttDate.getStartDateByIndex(v).format('M/D')
          : ganttDate.getEndDateByIndex(v).format('M/D')
      }}
      onBlur={onBulrSlider}
      componentsProps={
        props.editable
          ? {
              track: {
                onMouseDown: () => {
                  setIsDragging(true)
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithTrack(selectedRows)
                },
              },
              thumb: {
                onMouseDown: () => {
                  setIsDragging(true)
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithThumb(selectedRows)
                },
              },
            }
          : undefined
      }
      onChange={(event: Event, val: number | number[], activeThumb: number) => {
        if (!props.editable) return
        const indexes = typeof val === 'number' ? [val, val] : val
        if (event.type === 'mousedown') {
          ganttIndex.setMoveWithTrackOrigin(indexes, activeThumb)
        }
        if (event.type !== 'mousemove') return
        ganttIndex.move(indexes, activeThumb)
      }}
      onChangeCommitted={() => {
        setIsDragging(false)
        onEdit()
      }}
      max={props.timeScale.length}
      wbsitemtype={wbsItem.wbsItemType}
      scheduled={
        +(
          !!wbsItem.scheduledDate?.startDate && !!wbsItem.scheduledDate?.endDate
        )
      }
      disablethumbs={[!!ganttIndex.startDisabled, !!ganttIndex.endDisabled]}
      index={props.node?.rowIndex ?? 0}
      isv2={props.isV2 ? 1 : 0}
      overrunleft={props.overrunLeft}
      overrunwidth={props.overrunWidth}
    />
  )

  if (!props.isV2) {
    return (
      <Fade in={true} timeout={300}>
        {slider}
      </Fade>
    )
  }

  const visibleLeft = Math.max(0, trackLeftPercent)

  const labelContentProps = {
    isParentType,
    displayOptions,
    wbsItemForDisplay,
    progressRate,
    labelMode,
    hasCumulation: hasChildrenToAggregate && !wbsItem.wbsItemType?.isTask(),
  }
  const hasInfoContent = hasAnyContent(labelContentProps)
  const hoverText = buildHoverText(labelContentProps)

  return (
    <Fade in={true} timeout={300}>
      <SliderWrapper ref={wrapperRef}>
        {slider}
        {progressRate > 0 &&
          isBarVisible &&
          props.isV2 &&
          wbsItem.scheduledDate?.startDate &&
          wbsItem.scheduledDate?.endDate && (
            <ProgressGauge
              style={{
                left: `${Math.max(0, trackLeftPercent)}%`,
                width: `${Math.max(0, Math.min(100, progressEndPercent) - Math.max(0, trackLeftPercent))}%`,
              }}
              $barHeight={isParentType ? 32 : 20}
            />
          )}
        {isBarVisible && hasInfoContent && (
          <Tooltip
            title={hoverText}
            placement="top"
            arrow={true}
            componentsProps={{
              tooltip: {
                sx: { maxWidth: 450 },
              },
            }}
          >
            {labelMode === 'hover' ? (
              <BarHoverArea $left={visibleLeft} $width={barWidthPercent} />
            ) : labelMode === 'inside' ? (
              <InsideLabel
                ref={innerLabelRef}
                $left={visibleLeft}
                $width={barWidthPercent}
                $isParent={isParentType}
              >
                <GanttLabelContent {...labelContentProps} />
              </InsideLabel>
            ) : labelMode === 'right' ? (
              <RightLabel
                $left={barEndPercent}
                $isParent={isParentType}
                $maxWidth={`${rightSpacePercent}%`}
              >
                <GanttLabelContent {...labelContentProps} />
              </RightLabel>
            ) : (
              <LeftLabel
                $barLeft={leftSpacePercent}
                $isParent={isParentType}
                $maxWidth={`${leftSpacePercent}%`}
              >
                <GanttLabelContent {...labelContentProps} />
              </LeftLabel>
            )}
          </Tooltip>
        )}
      </SliderWrapper>
    </Fade>
  )
}
